import {Helmet} from '@dr.pogodin/react-helmet';
import {FlagProps, openDeleteDialog, Parameter, showNotification} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {defaultTo, isNil} from 'ramda';
import {concatAll, isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useDeleteReceiveNoteCorrectionMutation,
  useGetReceiveNoteCorrectionQuery,
  useGetReceiveNoteQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  handleApiError,
  NavigationItem,
  createHeaderActions,
  Section,
  useChecklist,
  usePermissions,
  printPDF,
  getUserName,
  useFileNotification,
} from '@dms/shared';
import {createHeaderFlags} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';

export function ReceiveNoteCorrectionDetail() {
  const {receiveNoteCreateId, receiveNoteCorrectionId} = useWarehouseParams();
  const fileNotification = useFileNotification({timeoutMs: 30000});

  const navigate = useNavigate();

  const dateTimeFormatter = useDateTimeFormatter();

  const [receiveNoteCorrectionEdit] = usePermissions({
    permissionKeys: ['receiveNoteCorrectionEdit'],
  });

  const {
    data: receiveNote,
    isLoading: isReceiveNoteLoading,
    isError: hasReceiveNoteError,
  } = useGetReceiveNoteQuery(
    {receiveNoteId: receiveNoteCreateId},
    {skip: isNilOrEmpty(receiveNoteCreateId)}
  );

  const {data: receiveNoteCorrectionAction, isFetching: isReceiveNoteCorrectionActionFetching} =
    useGetWarehouseHeaderDynamicActionsQuery(
      {id: receiveNoteCorrectionId, resource: 'credit-note'},
      {skip: isNilOrEmpty(receiveNoteCorrectionId)}
    );

  const {
    data: receiveNoteCorrection,
    isLoading: isReceiveNoteCorrectionLoading,
    isFetching: isReceiveNoteCorrectionFetching,
    isError: hasReceiveNoteCorrectionError,
  } = useGetReceiveNoteCorrectionQuery(
    {creditNoteId: receiveNoteCorrectionId},
    {skip: isNilOrEmpty(receiveNoteCorrectionId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-credit-note', contextId: receiveNoteCorrectionId},
    {skip: !receiveNoteCorrectionId}
  );

  const [putReceiveNoteCorrectionAction, {isLoading: isReceiveNoteCorrectionActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const [deleteReceiveNoteCorrection] = useDeleteReceiveNoteCorrectionMutation();

  const createdByUserQuery = useGetUserQuery(
    {id: receiveNoteCorrection?.creditNote?.createdBy as string},
    {skip: isNil(receiveNoteCorrection?.creditNote?.createdBy)}
  );

  const updatedByUserQuery = useGetUserQuery(
    {id: receiveNoteCorrection?.creditNote?.updatedBy as string},
    {skip: isNil(receiveNoteCorrection?.creditNote?.updatedBy)}
  );

  const completedByUserQuery = useGetUserQuery(
    {id: receiveNoteCorrection?.creditNote?.completedBy as string},
    {skip: isNil(receiveNoteCorrection?.creditNote?.completedBy)}
  );

  const isLoading =
    isReceiveNoteLoading ||
    isReceiveNoteCorrectionLoading ||
    createdByUserQuery.isLoading ||
    updatedByUserQuery.isLoading ||
    completedByUserQuery.isLoading;

  const hasError = hasReceiveNoteError || hasReceiveNoteCorrectionError;

  const isDraftReceiveNoteCorrection = isNil(receiveNoteCorrection);
  const isCompletedReceiveNoteCorrection = receiveNoteCorrection?.creditNote?.state === 'COMPLETED';

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUserQuery.data) ?? EMPTY_PLACEHOLDER}`;
  const createdAt = `${i18n.t('general.labels.createdAt')}: ${formatToShortDate(receiveNoteCorrection?.creditNote?.createdAt)}`;

  const updatedBy = `${i18n.t('general.labels.updatedBy')}: ${getUserName(updatedByUserQuery.data) ?? EMPTY_PLACEHOLDER}`;
  const updatedAt = `${i18n.t('general.labels.updatedAt')}: ${formatToShortDate(receiveNoteCorrection?.creditNote?.updatedAt)}`;

  const completedBy = `${i18n.t('general.labels.completedBy')}: ${isCompletedReceiveNoteCorrection ? getUserName(completedByUserQuery.data) : EMPTY_PLACEHOLDER}`;
  const completedAt = `${i18n.t('general.labels.completedAt')}: ${isCompletedReceiveNoteCorrection ? formatToShortDate(receiveNoteCorrection?.creditNote?.updatedAt) : EMPTY_PLACEHOLDER}`;

  const headerTitle = isNilOrEmpty(receiveNoteCorrectionId)
    ? i18n.t('entity.warehouse.labels.newReceiveNoteCorrection')
    : `${i18n.t('entity.warehouse.labels.receiveNoteCorrection')} ${defaultTo('', receiveNoteCorrection?.creditNote?.creditNoteNumber)}`;

  const headerParameters = match(isDraftReceiveNoteCorrection)
    .returnType<Parameter[]>()
    .with(true, () => [])
    .otherwise(() => [createdBy, createdAt, updatedBy, updatedAt, completedBy, completedAt]);

  const markersFlags = createHeaderFlags(receiveNoteCorrection?.creditNote?.markers);

  const staticFlags = match(receiveNoteCorrection?.creditNote?.state)
    .returnType<FlagProps[]>()
    .with('PENDING', () => [{colorScheme: 'blue', label: i18n.t('general.labels.pending')}])
    .with('COMPLETED', () => [{colorScheme: 'green', label: i18n.t('general.labels.completed')}])
    .otherwise(() => [{colorScheme: 'neutral', label: i18n.t('entity.warehouse.labels.draft')}]);

  const headerFlags = concatAll<FlagProps[]>([staticFlags, markersFlags]);

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntCreditNote') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: receiveNoteCorrectionId,
          originEntityPrintoutType: 'prntCreditNote',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
            });
          } else {
            showNotification.error();
          }
        })
        .then(() => documentCountQuery.refetch())
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.receiveNoteCorrectionDetailDocuments, {
              params: {id: receiveNoteCorrectionId},
            })
          );
        })
        .catch(handleApiError);
    }

    if (actionKey === 'delete') {
      return openDeleteDialog({
        onConfirm: () =>
          deleteReceiveNoteCorrection({
            body: {creditNoteId: [receiveNoteCorrectionId]},
          })
            .unwrap()
            .then(() =>
              showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
            )
            .then(() => navigate(composePath(warehouseRoutes.receiveNoteCorrectionsList)))
            .catch(handleApiError),
        'data-testid': testIds.warehouse.receiveNoteCorrectionDetailOverview('delete'),
      });
    }

    putReceiveNoteCorrectionAction({
      id: receiveNoteCorrection?.creditNote.creditNoteId as string,
      resource: 'credit-note',
      body: {actionKey},
    })
      .unwrap()
      .then(() => documentCountQuery.refetch())
      .catch(handleApiError);
  };

  const headerActions = createHeaderActions({
    actions: receiveNoteCorrectionAction?.actions,
    callback: handleActionClick,
    isLoading:
      isReceiveNoteCorrectionActionLoading ||
      isReceiveNoteCorrectionActionFetching ||
      isPostPrintoutOnDemandLoading,
  });

  const header: DetailTemplateHeader = {
    title: headerTitle,
    isEntityLoading: isReceiveNoteCorrectionFetching,
    icon: 'action/deployed',
    parameters: headerParameters,
    recordId: receiveNoteCorrection?.creditNote?.creditNoteId,
    resourceId: EntityResourceIds.receiveNoteCorrection,
    flags: headerFlags,
    actions: headerActions,
    controls: ['ASSIGNEE'],
    alerts:
      !receiveNoteCorrectionEdit && !isDraftReceiveNoteCorrection
        ? [
            {
              type: 'banner',
              variant: 'info',
              message: i18n.t('module.warehouse.label.missingReceiveNoteCorrectionEditPermission'),
            },
          ]
        : undefined,
  };

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: receiveNoteCorrectionId || '',
    resourceId: 'RECEIVE_NOTE_CORRECTION',
  });

  const navigation = buildArray<NavigationItem>([
    {
      id: 'receiveNoteCorrectionDetail',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.receiveNoteCorrectionDetailOverview, {
        params: {id: receiveNoteCorrectionId},
      }),
      content: (
        <Overview
          receiveNote={receiveNote}
          receiveNoteCorrection={receiveNoteCorrection}
          isReceiveNoteCorrectionLoading={isReceiveNoteCorrectionLoading}
          data-testid={testIds.warehouse.receiveNoteCorrectionDetailOverview('sections.overview')}
        />
      ),
      'data-testid': testIds.warehouse.receiveNoteCorrectionDetailOverview('navigation.overview'),
    },
    {
      id: 'receiveNoteCorrectionDocuments',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.receiveNoteCorrectionDetailDocuments, {
        params: {id: receiveNoteCorrectionId},
      }),
      content: (
        <Documents
          onDocumentAction={() => documentCountQuery.refetch()}
          data-testid={testIds.warehouse.receiveNoteCorrectionDetailOverview('sections.documents')}
        />
      ),
      badge: !!documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.receiveNoteCorrectionDetailOverview('navigation.documents'),
    },
  ]).whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
    id: 'receiveNoteCorrectionChecklists',
    label: i18n.t('entity.checklist.labels.title'),
    href: composePath(warehouseRoutes.receiveNoteCorrectionDetailChecklists, {
      params: {id: receiveNoteCorrectionId},
    }),
    content: (
      <Section>
        <Checklists
          {...checklistSettings}
          contextTarget="warehouse-receipt-note"
          contextId={receiveNoteCorrectionId}
        />
      </Section>
    ),
    badge: badgeSettings,
    'data-testid': testIds.warehouse.receiveNoteCorrectionDetailChecklists('checklists'),
  });

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.receiveNoteCorrection')} />
      <DetailTemplate
        key={generateHashFromObjects(receiveNoteCorrection)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={isReceiveNoteCorrectionActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.receiveNoteCorrectionDetailOverview('page')}
      />
    </>
  );
}
